import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, BadgePercent } from "lucide-react";
import { CUBE_VAN_PRICING, LOAD_INCLUDES, ITEM_PRICING, DISPOSAL_FEES, DEMOLITION } from "../data/content";

const TABS = [
  { id: "items", label: "Individual Items", data: ITEM_PRICING, note: "Prices are starting rates. Final pricing may vary based on size, weight and accessibility." },
  { id: "disposal", label: "Special Disposal Fees", data: DISPOSAL_FEES, note: "Additional fees applied on top of load pricing for these regulated items." },
  { id: "demo", label: "Demolition & Removal", data: DEMOLITION, note: "Starting rates. Final pricing depends on size, materials and site access." },
];

const PriceTable = ({ data }) => (
  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-1">
    {data.map((row) => (
      <div key={row.item} className="flex items-center justify-between border-b border-dashed border-border py-2.5">
        <span className="text-foreground">{row.item}</span>
        <span className="font-semibold text-primary whitespace-nowrap">{row.price}</span>
      </div>
    ))}
  </div>
);

export const PriceGuide = () => {
  const [active, setActive] = useState("items");
  const tab = TABS.find((t) => t.id === active);

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 bg-secondary/30">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mb-12">
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">Standard cube van pricing</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            All-inclusive pricing. No hidden fees.
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            Pricing is based on how much space your junk takes up in our cube van. Every load includes labour, hauling, disposal and a sweep-up.
          </p>
        </div>

        {/* Cube van load pricing */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CUBE_VAN_PRICING.map((p, i) => (
              <motion.div
                key={p.size}
                data-testid={`cubevan-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`rounded-sm p-6 border ${
                  p.highlight
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border hover:border-accent"
                } transition-colors`}
              >
                <h3 className={`text-base font-semibold tracking-tight ${p.highlight ? "text-primary-foreground" : "text-foreground"}`}>{p.size}</h3>
                {p.desc && <p className={`text-xs mt-0.5 ${p.highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{p.desc}</p>}
                <p className={`mt-4 text-3xl font-bold tracking-tight ${p.highlight ? "text-[hsl(var(--brand-green))]" : "text-primary"}`}>{p.price}</p>
              </motion.div>
            ))}
          </div>

          {/* Every load includes */}
          <div className="rounded-sm border border-accent/30 bg-card p-7">
            <h3 className="text-lg font-semibold tracking-tight text-foreground">Every load includes</h3>
            <ul className="mt-4 space-y-3">
              {LOAD_INCLUDES.map((t) => (
                <li key={t} className="flex items-center gap-3 text-foreground">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-accent text-white shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-center gap-2 rounded-sm bg-[hsl(var(--brand-green))]/15 text-accent font-semibold px-4 py-3">
              <BadgePercent className="w-5 h-5" /> 10% OFF Curbside Pickups
            </div>
          </div>
        </div>

        {/* Detailed pricing tabs */}
        <div className="mt-12 rounded-sm border border-border bg-card p-6 md:p-8">
          <div className="flex flex-wrap gap-2 border-b border-border pb-4">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`price-tab-${t.id}`}
                onClick={() => setActive(t.id)}
                className={`rounded-sm px-4 py-2 text-sm font-semibold transition-colors ${
                  active === t.id ? "bg-primary text-primary-foreground" : "bg-muted text-foreground hover:bg-secondary"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="mt-6">
            <PriceTable data={tab.data} />
            <p className="mt-5 text-xs text-muted-foreground">*{tab.note}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
